<template>
    <div>
        <header class="mb-6">
            <Link
                :href="route('web.catalog.manufacturers.show', product?.vendor?.slug)"
                v-if="product?.vendor?.slug"
                class="w-fit text-secondary font-medium at1024:font-normal text-base at1024:text-sm at1200:text-base hover:underline underline-offset-4"
            >
                {{ product.vendor.display_name }}
            </Link>
            <h1 class="break-words font-bold text-primary text-2xl at1200:text-3xl">{{ product.sku.toUpperCase() }}</h1>
            <p class="text-base at1024:text-sm at1200:text-base text-gray">
                {{ description }}
            </p>
        </header>
        <div
            v-if="finish_swatch && finish.value"
            class="group relative w-fit flex items-center gap-1.5 text-base at1024:text-sm at1200:text-base mb-6 print:hidden"
        >
            <img class="rounded-full w-6 h-6 p-0.5 border border-gray-300"
                @mouseover="hintSeen=true"
                @mouseleave="hintSeen=false"
                :src="finish_swatch"
                :alt="finish.description"
                dusk="products-show-finish"
            />
            <span>{{ finish.value }}</span>
            <HintTransition v-if="finish.description" :hint-seen="hintSeen" name="fade-right">
                {{ finish.description }}
            </HintTransition>
        </div>
        <div v-if="user" class="block border-b mb-4 pb-4 print:border-b-0 print:mb-0 print:pb-0">
            <div v-if="isLoadingMeta" class="w-64 shrink-0">
                <LoadingSpinner :large="false" />
            </div>
            <div v-else class="flex items-end gap-3 mb-1.5">
                <div v-if="availability?.total < 1 && product?.status === 'D'" class="font-medium text-orange"
                    :class="hideCostDuringPrint ? 'print:hidden' : ''">{{ $t('Discontinued') }}</div>
                <div v-else-if="pricing?.price > 0 && !['S','N'].includes(product?.status)" :class="hideCostDuringPrint ? 'print:hidden' : ''">
                    <div dusk="products-show-price" class="font-bold !leading-none text-lg at1200:text-2xl">{{ showMeTheMoney(pricing.price) }}</div>
                </div>
                <div v-else :class="hideCostDuringPrint ? 'print:hidden' : ''">
                    <!-- <strong class="!leading-none text-lg at1024:text-xl at1200:text-2xl">{{ $t('Call') }}</strong> -->
                     <ChatButton />
                </div>
                <div dusk="products-show-list-price" v-if="product?.pricing_warehouse_item?.list_price > 0 && !['S','N'].includes(product?.status)" class="type-secondary whitespace-nowrap">
                    {{ showMeTheMoney(product.pricing_warehouse_item.list_price) }} {{ $t('list') }}
                    <span v-if="product.pricing_warehouse_item.fut_list_eff_date && product.pricing_warehouse_item.last_fut_update && product.pricing_warehouse_item.fut_list_price">
                        ({{ product.pricing_warehouse_item.fut_list_price == 0 ?
                        DateTime.fromISO(product.pricing_warehouse_item.fut_list_eff_date).toFormat('D') :
                        DateTime.fromISO(product.pricing_warehouse_item.last_fut_update).toFormat('D') }})
                    </span>
                </div>
            </div>

            <SurchargeNotice
                :surcharge="surcharge"
                :productSlug="product?.slug"
            />
        </div>
        <div v-else class="pb-6 text-base at1024:text-2xl font-bold">
            <span v-if="product?.pricing_warehouse_item?.list_price">
                {{ showMeTheMoney(product.pricing_warehouse_item.list_price) }} <span class="type-secondary text-base font-normal">{{ $t('list') }}</span>
            </span>
            <span v-else>{{ $t('Call') }}</span>
        </div>
        <div v-if="user && availability" class="overflow-visible">
            <div class="mb-6">
                <AvailabilityList
                    :availability="availability"
                    :pricing="pricing"
                    :card="card"
                    :is-loading-meta="isLoadingMeta"
                    class="print:hidden"
                >
                    <BaseSvg name="icon-check-outlined" class="shrink-0" v-if="availability?.total > 0 && pricing?.price" />
                </AvailabilityList>
            </div>
        </div>

        <!-- SEC-3099  -->
        <template v-if="user && master_note_restriction?.length">
            <div class="bg-peach items-center mb-6 p-4 text-sm">
                <span class="font-bold">Restriction: </span>
                <span class="if-list">
                    <span>{{master_note_restriction}}</span>
                </span>
            </div>
        </template>

        <!-- SEC-2342 -->
        <template v-if="user && product?.series?.product_alert">
            <div class="bg-peach items-center mb-6 p-4 text-sm">
                <span class="if-list">
                    <span v-html="product?.series?.product_alert"></span>
                </span>
            </div>
        </template>
        <template v-else >
            <template v-if="options && user">
                <div v-for="(group, key) in options" :id="key" class="px-px">
                    <SearchSelect
                        class="mb-3"
                        v-for="(option, key) in group"
                        :options="option"
                        :label="key"
                        :default-value="
                            Object.keys(
                                _.pickBy(option, (value) => {
                                    return (
                                        value.toLowerCase().includes('std') ||
                                        value.toLowerCase().includes('standard')
                                    );
                                })
                            )[0]
                        "
                        v-model="productOptions[key]"
                        :card="card"
                    />
                </div>
            </template>
            <p class="bg-blue-100 mb-[18px] px-[17px] py-[15px] text-blue text-sm" v-if="_.get(card,'legacy_tags',[]).includes('value-center')">
                This product is currently part of our Value Center promotion and aggressively discounted off of list price. This is a discontinued, overstock or closeout item. Sale is subject to stock on-hand. No factory or backorders will be accepted. We reserve the right to limit quantity sold. All sales are final (no returns).
            </p>

            <div
                v-if="user && pricing?.price && (product?.status !== 'D' || availability?.total > 0) && !['S','N'].includes(product?.status)"
                class="print:!hidden block at768:flex items-center gap-3 w-full h-auto at1024:h-12"
            >
                <div class="h-12 mb-4 at768:mb-0">
                    <Counter
                        v-model="quantity"
                        class="mb-4 at768:mb-0 w-full at768:w-auto"
                    />
                </div>
                <button
                    dusk="products-show-button-add-to-cart"
                    @click="
                        eventBus.emit('cart:add-item', {
                            sku:card.item_id,
                            quantity,
                            options: { specification_group: specification_group },
                            meta: {...meta, 'has_finish_alert': hasFinishAlert },
                        })
                    "
                    :disabled="isLoading || !pricing.price"
                    class="duration-150 btn-white-on-black !capitalize w-full rounded-full flex justify-center items-center whitespace-nowrap disabled:opacity-100 disabled:bg-opacity-50"
                >
                    <LoadingSpinner :large="false" v-if="isLoading && false" class="!border-white" />
                    <span v-else>{{ $t("Add to cart") }}</span>
                </button>
            </div>
        </template>

        <div v-if="user" class="print:hidden">
            <p
                v-if="isBuildable"
                class="mt-6 type-secondary text-sm text-left bg-blue-100 p-4 rounded"
            >
                {{ $t("We build to configure the exact specs for") }} <span href="#" class="font-medium">{{ product.sku }}</span>
                {{ $t("from components on-hand") }}.
                <span v-if="showBuildButton"
                    class="type-white decoration-dotted underline underline-offset-4 hover:decoration-solid hover:type-primary cursor-pointer"
                    @click="openChat"
                >
                    {{ $t("Start a build request") }}.
                </span>
                <span v-else
                    class="inline-block"
                >
                    <ChatButton />
                </span>
            </p>
        </div>
        <div v-else class="type-primary text-sm at1200:text-base">
            <p class="mb-8">
                {{ $t("Please") }} <Link
                :href="route('login')"
                class="text-red hover:underline hover:type-primary"
            >{{ $t("login") }}
            </Link> {{ $t("to see pricing, availability, resources, and product details") }}.
            </p>
            <div class="p-8 bg-gray-120">
                <p>
                    {{ $t("If you are in the security or related industry, you can") }} <Link
                    :href="route('register')"
                    class="text-red hover:underline hover:type-primary"
                >{{ $t("sign up") }}
                </Link> {{ $t("for an account. If you would like to find a dealer in your area, please email") }} <a
                    href="mailto:info@seclock.com"
                    class="text-red hover:underline hover:type-primary"
                >info@SECLOCK.com</a>.
                </p>
            </div>

        </div>
    </div>
</template>

<script setup>
import { DateTime } from "luxon";
import { eventBus, useHelpers, useSegment, useUser, useChat } from "@/Composables";
import {
    AvailabilityList,
    BaseSvg,
    Counter,
    HintTransition,
    SearchSelect,
    LoadingSpinner,
    ChatButton,
    SurchargeNotice,
} from "@/Components";
import { computed, inject, onMounted, ref, watch } from "vue";
import _ from "lodash";

const { user } = useUser();
const props = defineProps([
    "product",
    "pricing",
    "availability",
    "options",
    "finish",
    "card",
    "isLoadingMeta",
    "hideCostDuringPrint",
    "finish_swatch",
    "master_note_restriction",
    "showBuildButton",
    "surcharge",
]);
const route = inject("route");
const { showMeTheMoney } = useHelpers();
const isLoading = ref(false);

const { showNewMessage } = useChat();
const openChat = () => {
    let message = "Connecting you now with a Technical Sales Rep for " + (props.card ? props.card.item_id : "") + " build quantities. How many do you need?"
    showNewMessage(message)
};

eventBus.on("cart:add-item", () => {
    isLoading.value = true;
});
eventBus.on("cart:product-added", () => {
    isLoading.value = false;
});
eventBus.on("cart:add-item-cancelled", () => {
    isLoading.value = false;
});
let isOpen = ref(null);
let hintSeen = ref(false);

let quantity = ref(1);
let productOptions = ref({});
let meta = ref([]);

const { track } = useSegment()

const description = computed(() => {
    let desc = _.get(props.product, "descriptions.product-description.0.short", "");
    if (!desc) {
        desc = _.get(props.card, "display_short_description", "");
    }
    return desc;
});




const isPriority = computed(() => ['A'].includes(props.product.status) && props.availability?.total < 1);

// This is the old EE code logic for showing the build request button.
// {if sld:ser_zero_inventory && (total < 1 && status != 'Discontinued') && '{exp:stash:priority}' != 1}
const isBuildable = computed(() => props.product.series?.is_buildable &&
    props.availability?.total < 1 &&
    !['D'].includes(props.product.status) &&
    !(['A'].includes(props.product.status) &&
        props.product.vendor?.is_inventory_plus &&
        ['Sargent','LCN','Schlage','Von Duprin','Schlage Electronics'].includes(props.product.vendor?.display_name))
)

const specification_group = computed(() => {
    let specification_group = {};
    Object.entries(productOptions.value).map((option) => {
        let spec_group = option[0];
        let option_key = option[1];
        Object.entries(props.options).map((options) => {
            return Object.entries(options[1]).map((option) => {
                if (option[0] === spec_group) {
                    specification_group[spec_group] = {
                        [option_key]: option[1][option_key],
                    };
                }
            });
        });
    });
    return specification_group;
});

const hasFinishAlert = computed(() => {
    let finish = props.finish.value;
    return (finish?.startsWith("625") || finish?.startsWith("629"))
});

onMounted(() => {
    eventBus.on('select:spec', (option) => {
        console.log('spec value', option[1]);
    });

    watch(isBuildable, (buildable) => buildable && track.zeroInventoryProductViewed(props.product.sku))
});

</script>

<style>
.if-list ul {
    list-style-type: disc;
    padding-left: 1em;
}
</style>
